import { z } from "zod";
import {
  BOT_DESCRIPTION_MAX_LENGTH,
  BOT_INSTRUCTIONS_MAX_LENGTH,
  BOT_NAME_MAX_LENGTH,
  BOT_TITLE_MAX_LENGTH,
  TimeZoneSchema,
} from "./domain.js";

/**
 * A bot export bundle is a gzip-compressed POSIX tar stream: `manifest.json` first, then
 * `files/<relative path>` entries. The byte-level format is specified in docs/bot-export-format.md.
 */
export const BOT_BUNDLE_FORMAT = "hrokbot.bot-bundle";
export const BOT_BUNDLE_VERSION = 1;
export const BOT_BUNDLE_MANIFEST_PATH = "manifest.json";
export const BOT_BUNDLE_FILES_PREFIX = "files/";
export const BOT_BUNDLE_MEDIA_TYPE = "application/gzip";
export const BOT_BUNDLE_FILE_SUFFIX = ".tar.gz";

const MIB = 1024 * 1024;

/**
 * Every bound that keeps one export or import small. Counters enforce these on the bytes actually
 * streamed, never on sizes a header or manifest declares.
 */
export const BOT_BUNDLE_LIMITS = {
  /** One file's bytes. */
  maxFileBytes: 512 * MIB,
  /** All file bytes together, uncompressed. */
  maxTotalBytes: 2048 * MIB,
  /** Files in the bundle; the export plan holds one record per file in memory. */
  maxEntries: 25_000,
  /** UTF-8 bytes of one relative path. */
  maxPathBytes: 500,
  /** UTF-8 bytes of one path segment. */
  maxSegmentBytes: 255,
  /**
   * The compressed upload accepted by import. It must hold every bundle an export can write under
   * the other limits, or an export would succeed and the same server would refuse to take it
   * back: `maxBundleGzipBytes(maxBundleTarBytes())` is about 2118 MiB, and the rest is room for
   * archives other tools wrote. bot-bundle.test.ts fails when either side moves alone. The reverse
   * proxy's body limit (infra/compose/nginx-behind-proxy.conf.example) must stay above this.
   */
  maxCompressedBytes: 2176 * MIB,
  /** manifest.json is parsed in memory. */
  maxManifestBytes: 8 * MIB,
  /** One PAX extended header is parsed in memory. */
  maxPaxHeaderBytes: 64 * 1024,
  maxMemoryDocuments: 100,
  maxMemoryDocumentBytes: 1 * MIB,
  maxMemoryTotalBytes: 4 * MIB,
  maxRoutines: 50,
  maxRoutinePromptChars: 20_000,
  maxSkippedListed: 500,
  /** Entries one walk of a skipped folder visits before its size is reported as unknown. */
  maxSkippedWalkEntries: 100_000,
} as const;

const TAR_BLOCK_BYTES = 512;

function roundUpToTarBlock(bytes: number): number {
  return Math.ceil(bytes / TAR_BLOCK_BYTES) * TAR_BLOCK_BYTES;
}

type BundleTarLimits = {
  readonly maxTotalBytes: number;
  readonly maxEntries: number;
  readonly maxPathBytes: number;
  readonly maxManifestBytes: number;
};

/**
 * The most uncompressed tar bytes an export can write under `limits`. The file bytes are bounded
 * by `maxTotalBytes` and the rest is framing, which is largest for a file whose path needs a PAX
 * header: one block for that header, the record `<length> path=files/<path>\n` rounded up to whole
 * blocks, one block for the file's own header and up to a block less one of padding after its
 * bytes. Then comes the manifest (a header and its bytes rounded up to blocks) and the two zero
 * blocks that end the archive.
 */
export function maxBundleTarBytes(limits: BundleTarLimits = BOT_BUNDLE_LIMITS): number {
  const recordBody = BOT_BUNDLE_FILES_PREFIX.length + limits.maxPathBytes + " path=".length + 1;
  // The length counts its own digits, which are at most one more than the digits of the rest.
  const record = recordBody + String(recordBody).length + 1;
  const framingPerFile =
    TAR_BLOCK_BYTES + roundUpToTarBlock(record) + TAR_BLOCK_BYTES + (TAR_BLOCK_BYTES - 1);
  return (
    limits.maxTotalBytes +
    limits.maxEntries * framingPerFile +
    TAR_BLOCK_BYTES +
    roundUpToTarBlock(limits.maxManifestBytes) +
    2 * TAR_BLOCK_BYTES
  );
}

/**
 * The most bytes gzip writes for `bytes` of input, which is what it writes for input that does not
 * compress at all (video, archives): stored blocks. This is zlib's own `deflateBound` for the
 * window and memory settings Node's gzip uses, `bytes + bytes/4096 + bytes/16384 + bytes/2^25`,
 * plus its constant and the 18 bytes of gzip header and trailer. Rounded up, never down.
 */
export function maxBundleGzipBytes(bytes: number): number {
  return (
    bytes +
    Math.ceil(bytes / 4096) +
    Math.ceil(bytes / 16_384) +
    Math.ceil(bytes / 2 ** 25) +
    13 +
    18
  );
}

/** Folders skipped at any depth: caches and tool output that rebuild themselves. */
export const BOT_BUNDLE_SKIPPED_DIRECTORIES: readonly string[] = [
  ".chrome-profile",
  ".chrome-rend",
  ".cache",
  "node_modules",
  "__pycache__",
  ".pytest_cache",
  ".mypy_cache",
  ".ruff_cache",
  ".next",
  ".turbo",
  ".venv",
  ".npm",
  ".pnpm-store",
];

export const BotBundleSkipReasonSchema = z.enum([
  "cache",
  "hidden",
  "symlink",
  "special",
  /** A name a bundle cannot carry: not valid text, too long, or clashing with another name. */
  "name",
]);
export type BotBundleSkipReason = z.infer<typeof BotBundleSkipReasonSchema>;

// biome-ignore lint/suspicious/noControlCharactersInRegex: control characters are what this rejects.
const CONTROL = /[\u0000-\u001f\u007f-\u009f]/;

/**
 * Memory documents live in the database, but they are exported to Markdown files elsewhere, so a
 * path that could leave its folder there is refused here too.
 */
export function checkMemoryDocumentPath(value: string): boolean {
  if (value.length === 0 || value.length > 200) return false;
  if (CONTROL.test(value) || value.includes("\\") || value.startsWith("/")) return false;
  return value.split("/").every((segment) => segment !== "" && segment !== "." && segment !== "..");
}

const MemoryDocumentPath = z
  .string()
  .refine(checkMemoryDocumentPath, "Not a relative document path");

export const BotBundleManifestSchema = z
  .object({
    format: z.literal(BOT_BUNDLE_FORMAT),
    version: z.literal(BOT_BUNDLE_VERSION),
    exportedAt: z.string().max(40),
    bot: z
      .object({
        name: z.string().trim().min(1).max(BOT_NAME_MAX_LENGTH),
        title: z.string().max(BOT_TITLE_MAX_LENGTH),
        description: z.string().max(BOT_DESCRIPTION_MAX_LENGTH),
        instructions: z.string().max(BOT_INSTRUCTIONS_MAX_LENGTH),
      })
      .strict(),
    memory: z
      .array(
        z
          .object({
            path: MemoryDocumentPath,
            content: z.string().max(BOT_BUNDLE_LIMITS.maxMemoryDocumentBytes),
          })
          .strict(),
      )
      .max(BOT_BUNDLE_LIMITS.maxMemoryDocuments),
    routines: z
      .array(
        z
          .object({
            name: z.string().min(1).max(80),
            prompt: z.string().min(1).max(BOT_BUNDLE_LIMITS.maxRoutinePromptChars),
            crons: z.array(z.string().min(1).max(200)).max(20),
            timezone: TimeZoneSchema,
          })
          .strict(),
      )
      .max(BOT_BUNDLE_LIMITS.maxRoutines),
    /** Names only. Values are never exported, so they are entered again after an import. */
    secretNames: z.array(z.string().min(1).max(200)).max(500),
    /** Totals as the exporter planned them. A reader never trusts them over what it streams. */
    files: z
      .object({
        count: z.number().int().min(0).max(BOT_BUNDLE_LIMITS.maxEntries),
        bytes: z.number().int().min(0).max(BOT_BUNDLE_LIMITS.maxTotalBytes),
      })
      .strict(),
    skipped: z
      .array(
        z
          .object({
            path: z
              .string()
              .min(1)
              .max(BOT_BUNDLE_LIMITS.maxPathBytes * 2),
            size: z.number().int().min(0).nullable(),
            reason: BotBundleSkipReasonSchema,
          })
          .strict(),
      )
      .max(BOT_BUNDLE_LIMITS.maxSkippedListed),
    /** Skipped entries beyond the listed ones. */
    skippedMore: z.number().int().min(0),
  })
  .strict();
export type BotBundleManifest = z.infer<typeof BotBundleManifestSchema>;

export type BotBundlePathResult = { ok: true; path: string } | { ok: false; reason: string };

const WINDOWS_DRIVE = /^[A-Za-z]:/;
// Bidirectional overrides and isolates make one name read as another.
const BIDI = /[‪-‮⁦-⁩]/;
const utf8 = new TextEncoder();

/**
 * Checks a path relative to the bot workspace. It is purely lexical; the importer still opens
 * every file without following links. Hidden root entries are refused so a bundle cannot plant
 * shell startup files or credentials in the top of a computer's home.
 */
export function checkBundleFilePath(value: string): BotBundlePathResult {
  const fail = (reason: string): BotBundlePathResult => ({ ok: false, reason });
  if (value.length === 0) return fail("empty path");
  if (utf8.encode(value).byteLength > BOT_BUNDLE_LIMITS.maxPathBytes) return fail("path too long");
  if (value.includes("�")) return fail("path is not valid UTF-8");
  if (CONTROL.test(value)) return fail("control character in path");
  if (BIDI.test(value)) return fail("bidirectional control in path");
  if (value.includes("\\")) return fail("backslash in path");
  if (value.startsWith("/")) return fail("absolute path");
  if (WINDOWS_DRIVE.test(value)) return fail("drive prefix in path");
  const segments = value.split("/");
  for (const segment of segments) {
    if (segment === "") return fail("empty path segment");
    if (segment === "." || segment === "..") return fail("relative segment in path");
    if (utf8.encode(segment).byteLength > BOT_BUNDLE_LIMITS.maxSegmentBytes) {
      return fail("path segment too long");
    }
  }
  if (segments[0]?.startsWith(".")) return fail("hidden entry at the workspace root");
  return { ok: true, path: value };
}

/**
 * The key two paths collide on in a case- and normalization-insensitive file system (the default
 * on macOS and Windows), so a bundle that is valid here still unpacks the same way there.
 */
export function bundlePathCollisionKey(value: string): string {
  return value.normalize("NFC").toLowerCase().normalize("NFC");
}

/**
 * Decides, one path at a time, which paths of a bundle can be unpacked together on a case- and
 * normalization-insensitive file system. The importer refuses a bundle with a clash and the
 * exporter leaves the clashing file out; both call this, so they cannot disagree. A path clashes
 * with an earlier one when their collision keys are equal, when it lies inside an earlier file, or
 * when an earlier path lies inside it (a file and a folder cannot share a name). A path that
 * clashes claims nothing.
 */
export function createBundlePathClaims(): (relative: string) => boolean {
  const files = new Set<string>();
  const folders = new Set<string>();
  return (relative) => {
    const key = bundlePathCollisionKey(relative);
    const segments = key.split("/");
    const ancestors = segments
      .slice(0, -1)
      .map((_, index) => segments.slice(0, index + 1).join("/"));
    if (files.has(key) || folders.has(key) || ancestors.some((ancestor) => files.has(ancestor))) {
      return false;
    }
    files.add(key);
    for (const ancestor of ancestors) folders.add(ancestor);
    return true;
  };
}

export const BotBundleImportResultSchema = z.object({
  botId: z.string(),
  /** False when a retry with the same key returned the bot an earlier request created. */
  created: z.boolean(),
  /** True when the name was already taken and a suffix was added. */
  renamed: z.boolean(),
  files: z.number().int().min(0),
  bytes: z.number().int().min(0),
  memory: z.number().int().min(0),
  /** Routines are always created paused. */
  routines: z.number().int().min(0),
});
export type BotBundleImportResult = z.infer<typeof BotBundleImportResultSchema>;

/** Header carrying the retry key of one import. */
export const BOT_IMPORT_KEY_HEADER = "x-rakazo-import-key";
export const BOT_IMPORT_KEY_PATTERN = /^[A-Za-z0-9_-]{16,64}$/;
